import {
  ConnectionStates,
  QualityLevel,
  StreamStates,
  avSelectors,
  useAvContext,
} from "@minervaproject/avsubsystem";
import { LoadingDarkIcon } from "@minervaproject/fds";
import { useEffect, useRef } from "react";
import { shallowEqual } from "react-redux";

import { useAppSelector } from "hooks/redux_hooks";
import { PROFILE_PROPERTIES, useUserProperties } from "hooks/use_user_properties";
import { ResourceType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";

import "./screenshare_resource.scss";

function ScreenshareVideo({ userId, isSelf }: { userId: string; isSelf: boolean }) {
  const videoElement = useRef<HTMLDivElement>(null);
  const avConnection = useAvContext();
  const connectionState = avSelectors.useConnectionState();
  const user = useUserProperties(
    userId,
    PROFILE_PROPERTIES.concat("is_performing_av_check", "is_speaking"),
  );
  const isReady =
    user?.is_performing_av_check === false && connectionState === ConnectionStates.connected;
  const STATE_NEEDS_PUBLISH = [StreamStates.initial, StreamStates.playable, StreamStates.failed];
  const STATE_NEEDS_SUBSCRIBE = [StreamStates.initial, StreamStates.failed];

  const screenshareStreamState = avSelectors.useScreenshareState(userId);
  const isLoading =
    !screenshareStreamState || screenshareStreamState.state === StreamStates.connecting;
  const shouldPublish =
    isSelf &&
    (!screenshareStreamState || STATE_NEEDS_PUBLISH.includes(screenshareStreamState.state));
  const shouldSubscribe =
    !isSelf &&
    (!screenshareStreamState || STATE_NEEDS_SUBSCRIBE.includes(screenshareStreamState.state));

  const isSubscribed = screenshareStreamState?.state === StreamStates.playable;

  const [shouldMuteAudio, reason] = useAppSelector(
    classroomSelectors.selectShouldMuteUserAudio(userId),
    shallowEqual,
  );

  useEffect(() => {
    if (shouldMuteAudio) {
      window.flightLogger.log(`[ScreenshareVideo] Muting audio for ${userId}: ${reason}`);
    } else {
      window.flightLogger.log(`[ScreenshareVideo] Unmuting audio for ${userId}: ${reason}`);
    }
  }, [shouldMuteAudio, reason, userId]);

  useEffect(() => {
    if (!isSelf && isSubscribed) {
      avConnection?.setRemoteScreenshareAudio(userId, !shouldMuteAudio);
    }
  }, [shouldMuteAudio, isSelf, isSubscribed, avConnection, userId]);

  // Publish is usually done in the controls but sometimes it can be triggered automatically here
  useEffect(() => {
    if (!(videoElement && videoElement.current) || !isReady || !avConnection) {
      return;
    }
    if (shouldSubscribe) {
      window.flightLogger.log(`[ScreenshareVideo] subscribe to screenshare ${userId}`);
      videoElement.current.id = avConnection.subscribeScreenshare(userId, true, true);
      //  Maybe manage this automatically in screenshare?
      avConnection.setQualityLevelForRemoteScreenshare(userId, QualityLevel.Screenshare);
    } else if (shouldPublish) {
      window.flightLogger.log(`[ScreenshareVideo] Requesting publish screenshare`);
      videoElement.current.id = avConnection.publishScreenshare();
    }
  }, [userId, avConnection, videoElement, isReady, shouldSubscribe, shouldPublish]);

  // handle cleanup when the component unmounts
  useEffect(() => {
    return () => {
      if (!isReady || !avConnection) {
        return;
      }
      if (!isSelf) {
        window.flightLogger.log(`[ScreenshareVideo] unsubscribe screenshare ${userId}`);
        avConnection.unsubscribeScreenshare(userId);
      }
    };
  }, [avConnection, isSelf, isReady, userId]);

  if (!user) {
    return null;
  }

  // TODO: Using a placeholder animation - we should replace this with the one we use in forum
  return (
    <div>
      {isLoading && <LoadingDarkIcon className="absolute-center" width={50} height={50} />}
      <div ref={videoElement} hidden={isLoading} />
    </div>
  );
}

function ScreenshareResource({ resource }: { resource: ResourceType }) {
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const userId = resource.uploader_id;
  const isSelf = currentUserId === userId;
  return (
    <div className="w-full h-full screenshare-resource">
      <ScreenshareVideo userId={userId} isSelf={isSelf} />
    </div>
  );
}

export { ScreenshareVideo, ScreenshareResource };
